import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import type { DraftSaleDetail } from './api';

export interface PosProduct {
  id: number;
  product_variant_id?: number | null;
  name: string;
  code: string;
  /** Unit selling price (net of on-product discount, before line tax). */
  price: number;
  cost?: number;
  unit: string;
  unit_sale_id?: number;
  category_id?: number;
  brand_id?: number;
  warehouse_id?: number;
  stock: number;
  image?: string | null;
  /** Tax rate as a percentage (e.g. 14 for 14%). */
  tax_rate?: number;
  /** 1 = exclusive, 2 = inclusive (matches backend). */
  tax_method?: 1 | 2;
  is_imei?: 0 | 1;
  /** Sold by weight — qty is a decimal weight in kg. */
  is_weighable?: boolean;
  price_type?: 'retail' | 'wholesale';
}

export interface CartLine {
  id: string; // composite line id
  product: PosProduct;
  qty: number;
  /** Per-line discount as percentage (0-100). */
  discount: number;
  /** Per-line note. */
  note?: string;
  imei_number?: string;
}

export interface PaymentLine {
  id: string;
  amount: number;
  payment_method_id: number;
  account_id?: number | null;
}

interface PosState {
  warehouseId: number | null;
  customerId: number | null;
  /** Cached display name of the selected customer — lets the header/cart show
   *  the right name instantly (and offline) without waiting on the brief API. */
  customerName: string | null;
  /** Details of a customer created OFFLINE (the selected customerId is negative).
   *  Sent with the sale so the backend creates + links the real client on sync. */
  pendingNewClient: { name: string; phone?: string; email?: string; adresse?: string } | null;
  lines: CartLine[];
  /** Cart-level discount as percentage. */
  discount: number;
  /** Cart-level shipping fee. */
  shipping: number;
  note: string;
  /** Draft this cart was resumed from (null for fresh cart). */
  resumedDraftId: number | null;
  kitchenOrderId: number | null;
  paymentLines: PaymentLine[];

  setWarehouse: (id: number | null) => void;
  setCustomer: (
    id: number | null,
    name?: string | null,
    newClient?: PosState['pendingNewClient'],
  ) => void;
  addProduct: (p: PosProduct, qty?: number) => void;
  removeLine: (lineId: string) => void;
  setQty: (lineId: string, qty: number) => void;
  setLineDiscount: (lineId: string, discount: number) => void;
  setLineNote: (lineId: string, note: string) => void;
  setDiscount: (discount: number) => void;
  setShipping: (shipping: number) => void;
  setNote: (note: string) => void;
  /** Loyalty points the customer is redeeming on this sale. */
  usedPoints: number;
  setUsedPoints: (n: number) => void;
  /** Applied coupon (null when none). */
  couponCode: string | null;
  couponDiscount: number;
  setCoupon: (code: string, discount: number) => void;
  clearCoupon: () => void;
  /** Applied gift card (null when none) and its available balance. */
  giftCardCode: string | null;
  giftCardBalance: number;
  setGiftCard: (code: string, balance: number) => void;
  clearGiftCard: () => void;
  setResumedDraftId: (id: number | null) => void;
  setPaymentLines: (lines: PaymentLine[]) => void;
  addPaymentLine: (line: PaymentLine) => void;
  removePaymentLine: (id: string) => void;
  updatePaymentLine: (id: string, patch: Partial<PaymentLine>) => void;
  hydrateFromDraft: (
    draft: DraftSaleDetail,
    productLookup: (productId: number) => PosProduct | undefined,
  ) => void;
  clear: () => void;
}

export const usePosStore = create<PosState>()(
  persist(
    (set) => ({
      warehouseId: null,
      customerId: null,
      customerName: null,
      pendingNewClient: null,
      lines: [],
      discount: 0,
      shipping: 0,
      note: '',
      usedPoints: 0,
      couponCode: null,
      couponDiscount: 0,
      giftCardCode: null,
      giftCardBalance: 0,
      resumedDraftId: null,
      kitchenOrderId: null,
      paymentLines: [],

      setWarehouse: (id) => set({ warehouseId: id }),
      // Switching customer resets any redeemed points (they belong to a customer).
      setCustomer: (id, name = null, newClient = null) =>
        set({ customerId: id, customerName: name, pendingNewClient: newClient, usedPoints: 0 }),
      setUsedPoints: (n) => set({ usedPoints: Math.max(0, Math.floor(Number(n) || 0)) }),
      setCoupon: (code, discount) =>
        set({ couponCode: code, couponDiscount: Math.max(0, Number(discount) || 0) }),
      clearCoupon: () => set({ couponCode: null, couponDiscount: 0 }),
      setGiftCard: (code, balance) =>
        set({ giftCardCode: code, giftCardBalance: Math.max(0, Number(balance) || 0) }),
      clearGiftCard: () => set({ giftCardCode: null, giftCardBalance: 0 }),

      addProduct: (p, qty = 1) =>
        set((s) => {
          // Weighable lines carry a decimal weight; round to 3 decimals so
          // floating-point noise never shows on the receipt.
          const added = p.is_weighable ? Math.round(qty * 1000) / 1000 : qty;
          const existing = s.lines.find(
            (l) =>
              l.product.id === p.id &&
              (l.product.product_variant_id ?? null) === (p.product_variant_id ?? null),
          );
          if (existing) {
            return {
              lines: s.lines.map((l) =>
                l.id === existing.id
                  ? { ...l, qty: Math.round((l.qty + added) * 1000) / 1000 }
                  : l,
              ),
            };
          }
          return {
            lines: [
              ...s.lines,
              {
                id: `${p.id}-${p.product_variant_id ?? 0}-${Date.now()}`,
                product: p,
                qty: added,
                discount: 0,
              },
            ],
          };
        }),

      removeLine: (lineId) =>
        set((s) => ({ lines: s.lines.filter((l) => l.id !== lineId) })),

      setQty: (lineId, qty) =>
        set((s) => ({
          lines: s.lines
            .map((l) => (l.id === lineId ? { ...l, qty: Math.max(0, qty) } : l))
            .filter((l) => l.qty > 0),
        })),

      setLineDiscount: (lineId, discount) =>
        set((s) => ({
          lines: s.lines.map((l) =>
            l.id === lineId ? { ...l, discount: Math.min(100, Math.max(0, discount)) } : l,
          ),
        })),

      setLineNote: (lineId, note) =>
        set((s) => ({
          lines: s.lines.map((l) => (l.id === lineId ? { ...l, note } : l)),
        })),

      setDiscount: (discount) => set({ discount: Math.min(100, Math.max(0, discount)) }),
      setShipping: (shipping) => set({ shipping: Math.max(0, shipping) }),
      setNote: (note) => set({ note }),
      setResumedDraftId: (id) => set({ resumedDraftId: id }),
      setPaymentLines: (paymentLines) => set({ paymentLines }),
      addPaymentLine: (line) =>
        set((s) => ({ paymentLines: [...s.paymentLines, line] })),
      removePaymentLine: (id) =>
        set((s) => ({ paymentLines: s.paymentLines.filter((p) => p.id !== id) })),
      updatePaymentLine: (id, patch) =>
        set((s) => ({
          paymentLines: s.paymentLines.map((p) => (p.id === id ? { ...p, ...patch } : p)),
        })),

      hydrateFromDraft: (draft, productLookup) =>
        set(() => {
          const lines: CartLine[] = (draft.details ?? [])
            .map((d) => {
              const p =
                productLookup(d.product_id) ??
                ({
                  id: d.product_id,
                  product_variant_id: d.product_variant_id ?? null,
                  name: d.name,
                  code: d.code,
                  price: Number(d.Unit_price) || 0,
                  unit: '',
                  unit_sale_id: d.sale_unit_id,
                  stock: 0,
                  tax_rate: Number(d.TaxNet) || 0,
                  tax_method: d.tax_method ?? 1,
                  price_type: d.price_type,
                } as PosProduct);
              return {
                id: `${d.product_id}-${d.product_variant_id ?? 0}-${Math.random()}`,
                product: { ...p, price: Number(d.Unit_price) || p.price },
                qty: Number(d.quantity) || 1,
                discount: Number(d.discount) || 0,
                imei_number: d.imei_number,
              };
            });
          return {
            customerId: draft.client_id,
            warehouseId: draft.warehouse_id,
            lines,
            discount: Number(draft.discount) || 0,
            shipping: Number(draft.shipping) || 0,
            note: draft.notes ?? '',
            resumedDraftId: draft.id,
          };
        }),

      clear: () =>
        set({
          lines: [],
          discount: 0,
          shipping: 0,
          note: '',
          usedPoints: 0,
          couponCode: null,
          couponDiscount: 0,
          giftCardCode: null,
          giftCardBalance: 0,
          resumedDraftId: null,
      kitchenOrderId: null,
          paymentLines: [],
          // Reset to the default customer after a sale / cart clear: nulling
          // the id lets the POS header re-apply the default client.
          customerId: null,
          customerName: null,
          pendingNewClient: null,
        }),
    }),
    {
      name: 'elshahd-pos-cart',
      partialize: (s) => ({
        warehouseId: s.warehouseId,
        customerId: s.customerId,
        customerName: s.customerName,
        pendingNewClient: s.pendingNewClient,
        lines: s.lines,
        discount: s.discount,
        shipping: s.shipping,
        note: s.note,
        resumedDraftId: s.resumedDraftId,
        kitchenOrderId: s.kitchenOrderId,
      }),
    },
  ),
);

// ─── selectors / derived totals ───────────────────────────────────────────

export interface PosTotals {
  itemsCount: number;
  subtotal: number;
  lineDiscountTotal: number;
  cartDiscountTotal: number;
  taxTotal: number;
  shipping: number;
  /** Monetary discount applied from redeemed loyalty points. */
  pointsDiscount: number;
  /** Monetary discount applied from an applied coupon. */
  couponDiscount: number;
  /** Monetary credit applied from a gift card. */
  giftCardAmount: number;
  grandTotal: number;
}

/**
 * Line-level math mirrors PosController@GetProductsByParametre:
 *
 *  - For tax_method = 1 (exclusive): Unit_price is the pre-tax price.
 *      net  = Unit_price * qty * (1 - line_discount%)
 *      tax  = net * tax_rate%
 *      line_total = net + tax
 *
 *  - For tax_method = 2 (inclusive): Unit_price already includes tax.
 *      gross = Unit_price * qty * (1 - line_discount%)
 *      tax   = gross * tax_rate / (100 + tax_rate)
 *      net   = gross - tax
 *      line_total = gross
 *
 * Cart-level discount is applied to the post-tax line_total (matches legacy
 * behaviour where `discount` is a flat percentage on the running total).
 */
export function computeTotals(
  s: Pick<PosState, 'lines' | 'discount' | 'shipping'> & {
    usedPoints?: number;
    couponDiscount?: number;
    giftCardBalance?: number;
    /** Settings: whether tax is applied at all (pos_settings.show_tax). */
    taxEnabled?: boolean;
    /** Settings: fallback tax rate % when a product has none (settings.default_tax). */
    defaultTaxRate?: number;
  },
  pointRate = 1,
): PosTotals {
  let subtotal = 0; // sum of Unit_price * qty (gross before line discount)
  let lineDiscountTotal = 0;
  let lineTotalAfterTax = 0;
  let taxTotal = 0;

  // Tax is settings-driven: globally off when taxEnabled === false; otherwise
  // each line uses its own rate, falling back to the settings default rate.
  const taxOn = s.taxEnabled !== false;
  for (const l of s.lines) {
    const unit = Number(l.product.price) || 0;
    const taxRate = taxOn
      ? Number(l.product.tax_rate) || Number(s.defaultTaxRate) || 0
      : 0;
    const taxMethod: 1 | 2 = (l.product.tax_method as 1 | 2) ?? 1;
    const gross = unit * l.qty;
    const lineDiscount = (gross * l.discount) / 100;
    subtotal += gross;
    lineDiscountTotal += lineDiscount;

    if (taxMethod === 2) {
      // Inclusive: gross-after-line-discount already contains tax
      const grossNet = gross - lineDiscount;
      const tax = (grossNet * taxRate) / (100 + taxRate);
      taxTotal += tax;
      lineTotalAfterTax += grossNet;
    } else {
      // Exclusive
      const net = gross - lineDiscount;
      const tax = (net * taxRate) / 100;
      taxTotal += tax;
      lineTotalAfterTax += net + tax;
    }
  }

  const cartDiscountTotal = (lineTotalAfterTax * s.discount) / 100;
  const totalBeforePoints = lineTotalAfterTax - cartDiscountTotal + s.shipping;
  // Loyalty redemption: cap the points discount at the order total so it can
  // never push the total negative.
  const pointsDiscount = Math.min(
    Math.max(0, (s.usedPoints ?? 0) * pointRate),
    Math.max(0, totalBeforePoints),
  );
  const afterPoints = totalBeforePoints - pointsDiscount;
  // Coupon discount applies after points, never below zero.
  const couponDiscount = Math.min(
    Math.max(0, s.couponDiscount ?? 0),
    Math.max(0, afterPoints),
  );
  const afterCoupon = afterPoints - couponDiscount;
  // Gift card credit applies last, capped at the remaining total and the card balance.
  const giftCardAmount = Math.min(
    Math.max(0, s.giftCardBalance ?? 0),
    Math.max(0, afterCoupon),
  );
  const grandTotal = afterCoupon - giftCardAmount;

  return {
    itemsCount: s.lines.reduce((a, l) => a + l.qty, 0),
    subtotal,
    lineDiscountTotal,
    cartDiscountTotal,
    taxTotal,
    shipping: s.shipping,
    pointsDiscount,
    couponDiscount,
    giftCardAmount,
    grandTotal: Math.max(0, grandTotal),
  };
}
